import React from 'react'
import classNames from 'classnames'

import { IProps } from './index'
import { WiredCardElement } from 'marketing-site/src/library/elements/WiredCardElement'
import wire1 from 'marketing-site/src/images/Wire-SVGs/Wire-1.svg'
import wire2 from 'marketing-site/src/images/Wire-SVGs/Wire-2.svg'
import wire3 from 'marketing-site/src/images/Wire-SVGs/Wire-3.svg'
import wire4 from 'marketing-site/src/images/Wire-SVGs/Wire-4.svg'
import wire5 from 'marketing-site/src/images/Wire-SVGs/Wire-5.svg'
import { useMediaQuery, mq } from '../../utils'
import { BrandOptions, useBrand } from '../../providers/brandProvider'
import { WiredCardsComponent as WiredCardsComponentDefault } from './component'

export const WiredCardsComponent = (props: IProps): React.ReactElement => {
  const theme = useBrand()
  if (theme === BrandOptions.default) return <WiredCardsComponentDefault {...props} />
  return <WiredCardsComponentLegacy {...props} />
}

const WiredCardsComponentLegacy = ({ cards }: IProps) => {
  const isMobile = useMediaQuery(`(${mq.mobile})`)

  const progressBarWrapperClasses = classNames('wired-cards__wrapper', {
    'wired-cards--scroll': isMobile,
  })

  const progressBarFillClasses = classNames('wired-cards__container', {
    'wired-cards--animate': !isMobile,
  })

  return (
    <div className={progressBarWrapperClasses} data-testid="wired-cards-container">
      <div className={progressBarFillClasses}>
        {cards.map((card, index) => (
          <div className="wired-collection-list" key={`card-${index + 1}`}>
            <WiredCardElement {...card} />
          </div>
        ))}
      </div>
      {!isMobile && (
        <div className="wired-cards__container wired-cards--animate">
          {cards.map((card, index) => (
            <div className="wired-collection-list" key={`card-${index + 1}`}>
              <WiredCardElement {...card} />
            </div>
          ))}
        </div>
      )}
      <div className="wired-cards__blurred-edges" />
      <style jsx>
        {`
          .wired-cards {
            &__wrapper {
              width: 100%;
              display: flex;
              position: relative;
              flex-flow: row nowrap;
              overflow: hidden;
              scrollbar-width: none;

              &:hover {
                .wired-cards--animate {
                  animation-play-state: paused;
                }
              }
            }

            &__container {
              display: flex;
              flex-flow: row nowrap;
              width: min-content;
              height: 21rem;
              position: relative;

              .wired-collection-list {
                margin-top: 1rem;
                margin-right: 2.174rem;

                &:first-child {
                  margin-left: 2.174rem;
                }

                &:last-child {
                  margin-right: 0;
                }
              }

              .wired-collection-list:nth-child(5n + 1) {
                position: relative;

                &:before {
                  content: '';
                  position: absolute;
                  right: 227px;
                  bottom: 53px;
                  width: 4.375rem;
                  height: 4.375rem;
                  background-image: url(${wire1});
                  background-repeat: no-repeat;
                  background-size: 54%;
                }

                &:after {
                  content: '';
                  position: absolute;
                  top: 109px;
                  left: 177px;
                  width: 100%;
                  height: 100%;
                  z-index: 10;
                  background-image: url(${wire2});
                  background-repeat: no-repeat;
                  background-size: 100%;
                  transform: scale(0.9) translate(-3%, 6%);
                }
              }

              .wired-collection-list:nth-child(5n + 2) {
                position: relative;

                &:after {
                  content: '';
                  position: absolute;
                  top: 150px;
                  right: -51px;
                  width: 3.188rem;
                  height: 100%;
                  background-image: url(${wire3});
                  background-repeat: no-repeat;
                  background-size: 100%;
                  transform: scaleY(0.9);
                }
              }

              .wired-collection-list:nth-child(5n + 3) {
                position: relative;

                &:after {
                  content: '';
                  position: absolute;
                  top: 64px;
                  left: 213px;
                  width: 100%;
                  height: 100%;
                  z-index: 10;
                  background-image: url(${wire4});
                  background-repeat: no-repeat;
                  background-size: 100%;
                  transform: scale(0.9) translate(-4%, 8%);
                }
              }

              .wired-collection-list:nth-child(5n + 4) {
                position: relative;

                &:after {
                  content: '';
                  position: absolute;
                  top: 156px;
                  right: -36px;
                  width: 2.375rem;
                  height: 120px;
                  background-image: url(${wire5});
                  background-repeat: no-repeat;
                  transform: scaleY(0.9);
                }
              }
            }

            &--animate {
              animation-duration: ${cards.length * 15}s;
              animation-iteration-count: infinite;
              animation-timing-function: linear;
              animation-name: scrolling;
            }
          }

          @keyframes scrolling {
            0% {
              transform: translateX(0);
            }
            100% {
              transform: translateX(-100%);
            }
          }

          @media (${mq.mobile}) {
            .wired-cards {
              &__wrapper {
                &:hover {
                  .wired-cards--animate {
                    animation-play-state: running;
                  }
                }
              }

              &--scroll {
                z-index: 1;
                animation: none;
                overflow: scroll hidden;
                -ms-overflow-style: none;
                scrollbar-width: none;

                &::-webkit-scrollbar {
                  width: 0 !important;
                }
              }
            }
          }

          @media (${mq.desktop}) {
            .wired-cards {
              &__container {
                .wired-collection-list {
                  margin-right: 3.125rem;
                  &:first-child {
                    margin-left: 3.125rem;
                  }
                  &:last-child {
                    margin-right: 0;
                  }
                }
                .wired-collection-list:nth-child(5n + 1) {
                  &:before {
                    bottom: 79px;
                    right: 279px;
                    width: 5.875rem;
                  }
                  &:after {
                    top: 40px;
                    left: 217px;
                    background-size: 100% 90%;
                    transform: scale(1) translate(0, 0);
                  }
                }
                .wired-collection-list:nth-child(5n + 2) {
                  &:after {
                    top: 120px;
                    transform: scaleY(1);
                  }
                }
                .wired-collection-list:nth-child(5n + 3) {
                  &:after {
                    top: 26px;
                    left: 268px;
                    width: 94%;
                    transform: scale(1) translate(0, 0);
                  }
                }
                .wired-collection-list:nth-child(5n + 4) {
                  &:after {
                    top: 123px;
                    right: -49px;
                    width: 3.188rem;
                    transform: scaleY(1);
                  }
                }
              }
            }
          }

          @media (${mq.desktopXLg}) {
            .wired-cards {
              &__wrapper {
                max-width: $viewport-max-width;
                margin: auto;
                mask-image: linear-gradient(
                  to right,
                  rgba(255, 255, 255, 0) calc(0% + 2rem),
                  white calc(0% + 6.25rem),
                  white calc(100% - 6.25rem),
                  rgba(255, 255, 255, 0) calc(100% - 2rem)
                );
              }
            }
          }
        `}
      </style>
    </div>
  )
}
